import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { sanitizeInternalPath } from '@/utils/url';
import { Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthCallback() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      console.log('[Callback] handleCallback START');
      try {
        // Get the hash fragment from the URL
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const error = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');
        const code = hashParams.get('code');
        const type = hashParams.get('type');

        console.log('[Callback] URL params - error:', error, 'code:', !!code, 'type:', type);

        // Get the original URL from localStorage (stored before auth redirect)
        const originalUrl = localStorage.getItem('authRedirectUrl') || null;
        console.log('[Callback] originalUrl from localStorage:', originalUrl);

        if (error) {
          console.log('[Callback] Error in URL params:', errorDescription);
          // Communicate error back to parent tab
          localStorage.setItem('auth_callback_error', JSON.stringify({
            error: errorDescription || 'Authentication failed',
            timestamp: Date.now()
          }));
          
          // Try to communicate with opener window
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              type: 'AUTH_ERROR',
              error: errorDescription || 'Authentication failed'
            }, window.location.origin);
          }
          
          toast.error('This link is invalid or expired. Please request a new link.');
          // Clean up stored redirect URL
          localStorage.removeItem('authRedirectUrl');
          navigate('/fancy-signin');
          return;
        }

        // If PKCE or magic link provides a code, exchange it explicitly
        if (code) {
          console.log('[Callback] Exchanging code for session...');
          try {
            await supabase.auth.exchangeCodeForSession(code);
            console.log('[Callback] Code exchange completed');
          } catch (e) {
            console.log('[Callback] Code exchange failed (fallback to getSession):', e);
            // Ignore and fallback
          }
        }

        // Exchange the code for a session or get current session
        console.log('[Callback] Calling getSession...');
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        console.log('[Callback] getSession result - hasSession:', !!session, 'error:', sessionError?.message);

        if (sessionError) {
          console.log('[Callback] Session error:', sessionError.message);
          // Communicate error back to parent tab
          localStorage.setItem('auth_callback_error', JSON.stringify({
            error: 'Failed to establish session',
            timestamp: Date.now()
          }));
          
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              type: 'AUTH_ERROR',
              error: 'Failed to establish session'
            }, window.location.origin);
          }
          
          toast.error('This link is invalid or expired. Please request a new link.');
          // Clean up stored redirect URL
          localStorage.removeItem('authRedirectUrl');
          navigate('/fancy-signin');
          return;
        }

        if (session) {
          console.log('[Callback] Session found, userId:', session.user.id);
          // Store successful auth in localStorage with timestamp for cross-tab communication
          localStorage.setItem('auth_callback_success', JSON.stringify({
            success: true,
            user: {
              id: session.user.id,
              email: session.user.email,
              name: session.user.user_metadata?.name || session.user.user_metadata?.full_name,
              phone: session.user.user_metadata?.phone
            },
            timestamp: Date.now()
          }));

          // Try to communicate with opener window
          if (window.opener && !window.opener.closed) {
            console.log('[Callback] Sending AUTH_SUCCESS to opener window');
            window.opener.postMessage({
              type: 'AUTH_SUCCESS',
              user: {
                id: session.user.id,
                email: session.user.email,
                name: session.user.user_metadata?.name || session.user.user_metadata?.full_name,
                phone: session.user.user_metadata?.phone
              }
            }, window.location.origin);
          }

          // Check if we have saved onboarding data
          const savedData = localStorage.getItem('businessSignupData');
          console.log('[Callback] savedData exists:', !!savedData);
          
          // Respect optional next param (preserved deep link)
          const urlParams = new URLSearchParams(window.location.search);
          const nextParam = sanitizeInternalPath(urlParams.get('next'));
          // Clean up stored redirect URL after successful auth
          const redirectUrl = nextParam || originalUrl;
          console.log('[Callback] redirectUrl:', redirectUrl);
          localStorage.removeItem('authRedirectUrl');
          
          // Wait for auth state to propagate, then redirect
          const checkAuthAndRedirect = async () => {
            console.log('[Callback] checkAuthAndRedirect - waiting 1500ms...');
            // Increased wait time for auth state to fully propagate
            setTimeout(async () => {
              console.log('[Callback] setTimeout fired - checking business profile...');
              // Check if user has completed onboarding by checking for business profile AND services.
              // Use limit(1) instead of .single() — duplicate rows (legacy bug) must not break auth.
              const { data: profileRows } = await supabase
                .from('business_profiles')
                .select('id, name, booking_url')
                .eq('user_id', session.user.id)
                .order('created_at', { ascending: false })
                .limit(1);

              const businessProfile = profileRows?.[0] ?? null;
              console.log('[Callback] business_profiles query - hasProfile:', !!businessProfile);

              let hasCompletedOnboarding = false;
              if (businessProfile) {
                // Check if user has services (indicates completed onboarding)
                const { data: services, error: servicesError } = await supabase
                  .from('business_services')
                  .select('id')
                  .eq('business_id', businessProfile.id)
                  .limit(1);

                console.log('[Callback] business_services query - count:', services?.length, 'error:', servicesError?.code);
                hasCompletedOnboarding = services && services.length > 0;
              }

              console.log('[Callback] hasCompletedOnboarding:', hasCompletedOnboarding);

              let destination: string;
              if (redirectUrl && (redirectUrl.includes('/magic-onboarding') || redirectUrl.includes('/onboarding'))) {
                // User was mid-onboarding — always send to v2 regardless of which version they were in
                destination = '/magic-onboarding-v2';
              } else if (redirectUrl) {
                // Other redirect URLs (deep links, settings pages) — respect them
                destination = redirectUrl;
              } else if (savedData) {
                // User had a saved onboarding draft
                destination = '/magic-onboarding-v2';
              } else if (hasCompletedOnboarding) {
                // Existing user with completed onboarding
                destination = '/dashboard';
              } else {
                // New user or incomplete onboarding
                destination = '/magic-onboarding-v2';
              }

              console.log('[Callback] NAVIGATING to:', destination);
              navigate(destination);
            }, 1500); // Increased from 500ms to 1500ms for better reliability
          };
          
          checkAuthAndRedirect();
        } else {
          console.log('[Callback] No session found');
          // No session found
          localStorage.setItem('auth_callback_error', JSON.stringify({
            error: 'No session found',
            timestamp: Date.now()
          }));
          
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              type: 'AUTH_ERROR',
              error: 'No session found'
            }, window.location.origin);
          }
          
          // Clean up stored redirect URL
          localStorage.removeItem('authRedirectUrl');
          
          // Redirect to original URL or fallback to onboarding
          const fallbackUrl = originalUrl || '/magic-onboarding-v2';
          console.log('[Callback] No session - NAVIGATING to:', fallbackUrl);
          navigate(fallbackUrl);
        }
      } catch (error) {
        console.error('[Callback] handleCallback ERROR:', error);
        
        // Communicate error back to parent tab
        localStorage.setItem('auth_callback_error', JSON.stringify({
          error: 'An error occurred during authentication',
          timestamp: Date.now()
        }));
        
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({
            type: 'AUTH_ERROR',
            error: 'An error occurred during authentication'
          }, window.location.origin);
        }
        
        toast.error('Authentication failed. Please try again.');
        
        // Clean up stored redirect URL
        localStorage.removeItem('authRedirectUrl');
        
        setTimeout(() => {
          // Use originalUrl from outer scope (already captured before localStorage was cleared)
          const fallbackUrl = originalUrl || '/magic-onboarding-v2';
          console.log('[Callback] Error recovery - NAVIGATING to:', fallbackUrl);
          navigate(fallbackUrl);
        }, 2000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center max-w-md p-6">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Email Verified Successfully!</h2>
        <p className="text-muted-foreground mb-4">
          Your email has been verified and you're now signed in.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <p className="text-blue-800 text-sm font-medium mb-2">
            ✨ Good news! Your original tab has been automatically updated.
          </p>
          <p className="text-blue-600 text-xs">
            You can now return to your original tab to continue with the onboarding process.
          </p>
        </div>
        <div className="flex items-center justify-center gap-2 mb-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Redirecting in a few seconds...</span>
        </div>
        <button 
          onClick={() => window.close()}
          className="text-sm text-blue-600 hover:text-blue-800 underline mb-2"
        >
          Close this tab manually
        </button>
        <p className="text-xs text-muted-foreground">
          Or return to your original tab to continue
        </p>
      </div>
    </div>
  );
}
